import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { listenForAuthChanges, signIn, signInWithGoogle, signUp, logOut, getUpcomingEvents as fetchUpcomingEvents, getFCMToken, savePushToken } from '../firebase';
import { navigate } from '../utils/navigation';
import toast from 'react-hot-toast';

import { PermissionKey, hasPermission as checkPermission, canAccessAdmin as checkCanAccessAdmin, canCheckIn as checkCanCheckIn, canManageRecords as checkCanManageRecords, isSuperUser } from '../utils/permissions';

interface AuthContextType {
  loading: boolean;
  user: any;
  profile: any;
  isSuper: boolean;
  hasPermission: (key: PermissionKey) => boolean;
  canAccessAdmin: boolean;
  canCheckIn: boolean;
  canManageRecords: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; isAdmin?: boolean; message?: string }>;
  signInWithGoogle: () => Promise<{ success: boolean; profileComplete?: boolean; isAdmin?: boolean; message?: string }>;
  signUp: (email: string, password: string, additionalData: any) => Promise<{ success: boolean; message?: string }>;
  logOut: () => Promise<void>;
  getUpcomingEvents: () => Promise<any[]>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<{ loading: boolean; user: any; profile: any }>({
    loading: true,
    user: null,
    profile: null,
  });

  useEffect(() => {
    return listenForAuthChanges((state: any) => {
      setAuthState({
        loading: false,
        user: state.user,
        profile: state.profile,
      });
    });
  }, []);

  // Inactivity auto-logout (90 days of inactivity, persisted in localStorage)
  useEffect(() => {
    if (!authState.user) return;

    const INACTIVITY_TIMEOUT_MS = 90 * 24 * 60 * 60 * 1000; // 90 days (~3 months)
    const lastActiveKey = `acs_last_active_${authState.user.uid}`;

    const getStoredTime = (): number => {
      const val = localStorage.getItem(lastActiveKey);
      return val ? parseInt(val, 10) : Date.now();
    };

    let lastActiveTime = getStoredTime();

    // Make sure we have a stored value initialized
    localStorage.setItem(lastActiveKey, lastActiveTime.toString());

    let intervalId: NodeJS.Timeout;

    const triggerLogout = async () => {
      console.warn(`[InactivityTimeout] Auto logging out user due to inactivity.`);
      try {
        localStorage.removeItem(lastActiveKey);
        await logOut();
        try {
          queryClient.clear();
        } catch (_) {}
        toast.error('You have been logged out due to inactivity.');
        navigate('/login');
        window.location.reload();
      } catch (err) {
        console.error('[InactivityTimeout] Error executing auto-logout:', err);
      }
    };

    let isCancelled = false;
    let lastStorageWriteTime = 0;

    const checkTimeout = async () => {
      const inactiveDuration = Date.now() - lastActiveTime;
      if (inactiveDuration > INACTIVITY_TIMEOUT_MS) {
        await triggerLogout();
        return true;
      }
      return false;
    };

    const resetTimer = () => {
      const now = Date.now();
      lastActiveTime = now;
      // Throttle localStorage writes to at most once every 15 seconds
      if (now - lastStorageWriteTime > 15_000) {
        lastStorageWriteTime = now;
        localStorage.setItem(lastActiveKey, now.toString());
      }
    };

    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Run check immediately on mount
    checkTimeout().then((loggedOut) => {
      if (isCancelled || loggedOut) return;

      activityEvents.forEach(event => {
        window.addEventListener(event, resetTimer, { passive: true });
      });

      // Check every 5 minutes
      intervalId = setInterval(async () => {
        if (!isCancelled) {
          await checkTimeout();
        }
      }, 5 * 60 * 1000);
    });

    return () => {
      isCancelled = true;
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
      if (intervalId) clearInterval(intervalId);
    };
  }, [authState.user]);

  // Silent FCM Token Registration/Refresh
  useEffect(() => {
    if (!authState.user) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    const registerFcmTokenSilently = async () => {
      try {
        const token = await getFCMToken();
        if (token) {
          await savePushToken(token);
          console.log('[AuthContext] Silent FCM token refresh succeeded:', token.slice(0, 10) + '...');
        }
      } catch (e) {
        console.warn('[AuthContext] Silent FCM token refresh failed:', e);
      }
    };

    // Run after a short delay to let login process complete
    const timeoutId = setTimeout(registerFcmTokenSilently, 3000);
    return () => clearTimeout(timeoutId);
  }, [authState.user]);

  const handleSignIn = useCallback(async (email: string, password: string) => {
    return await signIn(email, password);
  }, []);

  const handleSignInWithGoogle = useCallback(async () => {
    return await signInWithGoogle();
  }, []);

  const handleSignUp = useCallback(async (email: string, password: string, additionalData: any) => {
    return await signUp(email, password, additionalData);
  }, []);

  const handleLogOut = useCallback(async () => {
    await logOut();
    try {
      queryClient.clear();
    } catch (_) {}
  }, [queryClient]);

  const getUpcomingEvents = useCallback(async () => {
    return fetchUpcomingEvents();
  }, []);

  const isSuper = useMemo(() => isSuperUser(authState.profile), [authState.profile]);

  const hasPermission = useCallback((key: PermissionKey) => {
    return checkPermission(authState.profile, key);
  }, [authState.profile]);

  const canAccessAdmin = useMemo(() => {
    return checkCanAccessAdmin(authState.profile);
  }, [authState.profile]);

  const canCheckIn = useMemo(() => {
    return checkCanCheckIn(authState.profile);
  }, [authState.profile]);

  const canManageRecords = useMemo(() => {
    return checkCanManageRecords(authState.profile);
  }, [authState.profile]);

  const value = useMemo(() => ({
    ...authState,
    isSuper,
    hasPermission,
    canAccessAdmin,
    canCheckIn,
    canManageRecords,
    signIn: handleSignIn,
    signInWithGoogle: handleSignInWithGoogle,
    signUp: handleSignUp,
    logOut: handleLogOut,
    getUpcomingEvents
  }), [
    authState,
    isSuper,
    hasPermission,
    canAccessAdmin,
    canCheckIn,
    canManageRecords,
    handleSignIn,
    handleSignInWithGoogle,
    handleSignUp,
    handleLogOut,
    getUpcomingEvents
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
